import { createApi } from '@reduxjs/toolkit/query/react';
import { AssignableUsersReq, DateRangeReq, ExceptionsResponse } from '~weekly-planner/types/api';

import { apiClients, apiWeeklyPlannerAppointments } from '~constants/api';

import { ApiError, Request, rtkBaseQueryWrapper, unwrap, WithRefetchKey } from '~libs/axiosWrapper';
import { get } from '~libs/genericAction';
import { Appointment, AssignableUser, BuiltChange } from '~weekly-planner/lib/common';

export const typePrefix = 'weekly-planner-appointments';

type AppointmentsReq = DateRangeReq & WithRefetchKey;

export const appointmentsApi = createApi({
  reducerPath: 'weeklyPlannerAppointmentsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiWeeklyPlannerAppointments }),
  tagTypes: [
    'appointments',
    'appointments-on-hold',
    'appointments-exceptions',
    'appointments-assignable-users',
    'appointments-built-changes',
  ],
  endpoints: (builder) => ({
    getAppointments: builder.query<Appointment[], AppointmentsReq>({
      query: (arg) => {
        const { __refetchKey, ...rest } = (arg ?? {}) as AppointmentsReq;
        const params = { limit: 0, ...rest };
        return { url: '', method: 'GET', params };
      },
      providesTags: [{ type: 'appointments', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Appointment[]>(payload),
    }),
    getOnHold: builder.query<any[], DateRangeReq>({
      async queryFn(arg, _api, _extra, _baseQuery) {
        // explicit call to bypass baseUrl
        try {
          const { __refetchKey, ...params } = (arg ?? {}) as any;
          const payload = await get(`${apiClients}/hold-periods`, params, undefined, _api.signal);
          return { data: unwrap<any>(payload) };
        } catch (e: any) {
          const error: ApiError = {
            status: e.status ?? 'FETCH_ERROR',
            message: e.message ?? 'Unknown error in getOnHold',
            data: e.data,
          };

          return { error };
        }
      },
      providesTags: [{ type: 'appointments-on-hold', id: 'LIST' }],
    }),
    getExceptions: builder.query<ExceptionsResponse, DateRangeReq>({
      query: (arg) => {
        const { __refetchKey, ...params } = (arg ?? {}) as any;
        return { url: '/exceptions', method: 'GET', params };
      },
      providesTags: [{ type: 'appointments-exceptions', id: 'LIST' }],
      transformResponse: (payload) => unwrap<ExceptionsResponse>(payload),
    }),
    getAssignableUsers: builder.query<AssignableUser[], AssignableUsersReq & WithRefetchKey>({
      query: ({ id, key }) => {
        const ident = id ?? key;
        return { url: `/${ident}/assignable-users`, method: 'GET' };
      },
      providesTags: [{ type: 'appointments-assignable-users', id: 'LIST' }],
      transformResponse: (payload) => unwrap<AssignableUser[]>(payload),
    }),
    getBuiltChanges: builder.query<BuiltChange[], Request>({
      query: (params) => ({ url: '/build-changes', method: 'GET', params }),
      providesTags: [{ type: 'appointments-built-changes', id: 'LIST' }],
      transformResponse: (payload) => unwrap<BuiltChange[]>(payload),
    }),
  }),
});

export const {
  useGetAppointmentsQuery,
  useGetOnHoldQuery,
  useGetExceptionsQuery,
  useGetAssignableUsersQuery,
  useGetBuiltChangesQuery,
} = appointmentsApi;
